import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

import { env } from "@/env";

const inputSchema = z.object({
	name: z.string().min(1),
	email: z.string().email(),
	phone: z.string().optional(),
	cvBase64: z.string().min(1),
	cvName: z.string().min(1),
	jobPostingDocumentId: z.string().optional(),
});

const uploadResponseSchema = z.array(z.object({ id: z.number() }));

export const submitApplication = createServerFn({ method: "POST" })
	.inputValidator(inputSchema)
	.handler(async ({ data }) => {
		const writeToken = env.STRAPI_WRITE_TOKEN;
		const baseUrl = env.VITE_STRAPI_BASE_URL;

		// Step 1: upload CV to Strapi media library
		const bytes = Uint8Array.from(atob(data.cvBase64), (c) => c.charCodeAt(0));
		const blob = new Blob([bytes], { type: "application/pdf" });
		const uploadForm = new FormData();
		uploadForm.append("files", blob, data.cvName);

		const uploadRes = await fetch(`${baseUrl}/api/upload`, {
			method: "POST",
			headers: { Authorization: `Bearer ${writeToken}` },
			body: uploadForm,
		});

		if (!uploadRes.ok) {
			throw new Error("CV upload failed");
		}

		const uploaded = uploadResponseSchema.parse(await uploadRes.json());
		const cvId = uploaded[0]?.id;
		if (cvId === undefined) throw new Error("No media ID returned from upload");

		// Step 2: create job application record
		const appData: Record<string, unknown> = {
			name: data.name,
			email: data.email,
			cv: cvId,
		};
		if (data.phone) appData.phone = data.phone;
		if (data.jobPostingDocumentId !== undefined) {
			appData.job_posting = data.jobPostingDocumentId;
		}

		const appRes = await fetch(`${baseUrl}/api/job-applications`, {
			method: "POST",
			headers: {
				Authorization: `Bearer ${writeToken}`,
				"Content-Type": "application/json",
			},
			body: JSON.stringify({ data: appData }),
		});

		if (!appRes.ok) {
			throw new Error("Application submission failed");
		}

		return { success: true };
	});

// Converts a File to a base64 string (client-side helper, chunked to avoid stack overflow on large PDFs)
export async function fileToBase64(file: File): Promise<string> {
	const buffer = await file.arrayBuffer();
	const bytes = new Uint8Array(buffer);
	const chunks: string[] = [];
	const chunkSize = 0x8000;
	for (let i = 0; i < bytes.length; i += chunkSize) {
		chunks.push(String.fromCharCode(...bytes.subarray(i, i + chunkSize)));
	}
	return btoa(chunks.join(""));
}
